'use client';

import { useMemo, type ReactElement } from 'react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import type { UsePlpRefinementsReturn } from '@/components/eataly-plp-filters/lib/types';
import {
    inferFilterUiType,
    isBooleanToggleRefinement,
    toRefinementWithValues,
} from '@/components/eataly-plp-filters/lib/refinement-ui-registry';
import RefinementRenderer from '@/components/eataly-plp-filters/refinement-renderer';
import { cn } from '@/lib/utils';

export type EatalyPlpFiltersPanelProps = {
    plp: UsePlpRefinementsReturn;
};

export default function EatalyPlpFiltersPanel({ plp }: EatalyPlpFiltersPanelProps): ReactElement {
    const { refinements, hasActiveFilter, isFilterSelected, toggleFilter, isPending } = plp;
    const visibleRefinements = useMemo(
        () => refinements.filter((refinement) => inferFilterUiType(refinement) !== 'skip'),
        [refinements]
    );
    return (
        <div className="flex flex-col border-neutral-80 px-6">
            {visibleRefinements.map((refinement) => {
                const { attributeId, label } = refinement;

                const refinementWithValues = toRefinementWithValues(refinement);
                const isToggleRefinement = isBooleanToggleRefinement(refinement);

                const rendererProps = {
                    refinement: refinementWithValues,
                    isFilterSelected,
                    toggleFilter,
                    isPending,
                };

                if (isToggleRefinement) {
                    return (
                        <div key={attributeId} className={cn('transition-opacity duration-200')}>
                            <RefinementRenderer {...rendererProps} />
                        </div>
                    );
                }

                return (
                    <Accordion
                        key={attributeId}
                        type="single"
                        collapsible
                        defaultValue={hasActiveFilter(attributeId) ? attributeId : undefined}
                        className={cn('w-full transition-opacity duration-200')}>
                        <AccordionItem value={attributeId} className="border-neutral-80">
                            <AccordionTrigger className="body-copy-m font-bold text-neutral-10">
                                {label || attributeId}
                            </AccordionTrigger>
                            <AccordionContent className="pt-0 pb-4">
                                <RefinementRenderer {...rendererProps} />
                            </AccordionContent>
                        </AccordionItem>
                    </Accordion>
                );
            })}
        </div>
    );
}
